// CategoryIdListType.jsx
import React from 'react';

import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  CategoryI18nKey,
  CATEGORY_FEED_REMOTE_CONFIG_KEYS,
} from '../resource/categoryConstants.js';
import fetchFeeds, { getReturnType } from '../action/fetchFeeds.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';
import { generateCollectionPageJsonLd } from '../resource/generateCollectionPageJsonLd.js';
import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getSelectedFeedQueryOptions from '../selector/getSelectedFeedQueryOptions.js';
import {
  ITEM_COUNT_EAGER_RENDER,
  QueryPrefix,
  QueryType,
} from '../resource/feedConstants.js';
import { sanitizeFeedQuery } from '../resource/feedUtils.js';
import shouldSkipSSRFetch from '../resource/shouldSkipSSRFetch.js';
import {
  CLEAR_NETWORKING_NODES,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('CategoryIdListType loader()');
  const errorLog = legacySSRError.extend('CategoryIdListType loader()');
  log('start', { context, params, request });

  const isServer = typeof window === 'undefined';
  // The redux store is provided via loader context (server: requestContext, client: getContext).
  // Loaders dispatch directly into it — the same store that
  // is rendered / serialized to __PRELOADED_STATE__
  // the data is present before the first render (SSR and navigation).
  const store = context?.store;
  if (!store) {
    if (isServer) {
      errorLog('context.store is missing on server', { params });
      throw new Error(
        'CategoryIdRoute loader requires context.store (server: query requestContext)'
      );
    }
    log('context.store missing on client, skipping loader');
    return { params, shouldSkipHydration: true };
  }

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    let state = store.getState();

    const skipSSRFetch =
      isServer &&
      shouldSkipSSRFetch({
        requestUrl: new URL(request.url),
        state,
      });

    if (skipSSRFetch) {
      log('skip SSR fetch due to buster query');
      // shouldSkipHydration: incomplete result — keep it out of
      // __staticRouterHydrationData so the client loader re-runs on the
      // initial load instead of hydrating a data-less page.
      return { params, shouldSkipHydration: true };
    }

    // Get category feed from remote config based on listType
    const { categoryId, listType } = params;

    const feedNameRemoteConfigKey = CATEGORY_FEED_REMOTE_CONFIG_KEYS[listType];
    if (!feedNameRemoteConfigKey) {
      // Invalid listType, return noindex
      return {
        params,
        meta: [{ name: 'robots', content: 'noindex, nofollow' }],
        staticContext: {
          requestHeaders: {
            hostname: request?.headers?.get('host') || 'swag.live',
          },
        },
      };
    }

    const categoryFeedName = getRemoteConfigData(
      state,
      feedNameRemoteConfigKey,
      undefined,
      {
        CATEGORY: categoryId,
      }
    );

    const sortingsRemoteConfigKey = `${feedNameRemoteConfigKey}_${
      QueryPrefix[QueryType.SORT]
    }`;
    const { feedNameWithQuery } = getSelectedQueryOptionObject(
      state,
      categoryFeedName,
      getSelectedFeedQueryOptions(state, feedNameRemoteConfigKey, undefined),
      getRemoteConfigData(state, sortingsRemoteConfigKey)?.[0]
    );

    // Fetch category feed count and first page in parallel
    const [feedsResult, feedCountResult] = await Promise.allSettled([
      feedNameWithQuery && isServer
        ? store.dispatch(
            fetchFeeds({
              page: 1,
              limit: ITEM_COUNT_EAGER_RENDER,
              type: feedNameWithQuery,
              httpProxyHeaders,
            })
          )
        : Promise.resolve(null),
      feedNameWithQuery
        ? fetchFeedTotalCount({
            feedNameWithQuery,
            httpProxyHeaders,
          })
        : Promise.resolve(null),
    ]);

    if (
      feedsResult.status === 'fulfilled' &&
      feedsResult.value?.type === SET_NETWORKING_ERROR
    ) {
      const sanitizedType = sanitizeFeedQuery({ feedName: feedNameWithQuery });
      const returnType = getReturnType({ feedName: sanitizedType });
      const networkingClearPath = ['home', returnType, sanitizedType, 1].map(
        p => `${p}`
      );
      store.dispatch({
        type: CLEAR_NETWORKING_NODES,
        payload: { selectPaths: [networkingClearPath] },
      });
      return { params, shouldSkipHydration: true };
    }

    const feedCount =
      feedCountResult.status === 'fulfilled' ? feedCountResult.value : 0;

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, feedCount });

    // Use i18n instance from loader
    const { t } = i18nInstance;

    // Get category name and list type name from translation
    const categoryName = t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
    });
    const categoryListType = CategoryI18nKey[listType];

    // Prepare title and description
    const title = t(`seo_category_${categoryListType}_title`, {
      ns: TranslationNamespace.SEO,
      category: categoryName,
    });
    const description =
      t(`seo_category_${categoryId}_${categoryListType}_description`, {
        ns: TranslationNamespace.SEO,
      }) ||
      t(`seo_category_${categoryListType}_description`, {
        ns: TranslationNamespace.SEO,
        category: categoryName,
      });

    const availableLanguages = getSupportedLanguages(state);
    const canonicalPath = `/category/${categoryId}/${listType}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;
    const jsonLd = await generateCollectionPageJsonLd({
      origin,
      categoryId,
      listType,
      state,
      i18n: i18nInstance,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [categoryName, categoryListType, 'SWAG', 'category'],
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      jsonLd,
    });

    log('meta data prepared', { meta });

    return {
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    // shouldSkipHydration: a failed server fetch must not be hydrated, so the
    // client loader re-runs on the initial load and retries the fetch.
    return { params, shouldSkipHydration: true };
  }
};

const CategoryIdRoute = () => {
  return (
    <UniversalComponent
      filename="Container_MessageCategoryTabContent"
      importer={
        /* #__LOADABLE__ */ () =>
          import('../container/MessageCategoryTabContent.js')
      }
    />
  );
};

export default CategoryIdRoute;
